import { Injectable } from '@angular/core';
import { Project } from './project.interface';
import { set } from 'lodash-es';
import { ProjectCoreService } from './project-core.service';
import { Building } from '../building/building.interface';
import { Cluster, ClusterDragDropLocation } from '../cluster/cluster.interface';
import { ConvertResourceToJsonPipe } from '../../utilities/convert-resource-to-json/convert-resource-to-json.pipe';
import { Area } from '../area/area.interface';
import { ChaptersService } from '../../utilities/chapters/chapters.service';
import { PveFilterPipe } from '../../utilities/pve/pve-filter.pipe';
import { ProjectElement } from '../element/project-element.interface';
import { ProjectModule } from '../module/project-module.interface';
import { ProjectSubmodule } from '../submodule/project-submodule.interface';
import { BasicArea } from '../basic-area/basic-area.interface';
import { moveItemInArray } from '@angular/cdk/drag-drop';

/**
 * Project service.
 *
 * The public interface for the ProjectCoreService, with shorthands for
 * each operation.
 */
@Injectable({
  providedIn: 'root'
})
export class ProjectService extends ProjectCoreService {

  /** Constructor. */
  constructor(
    protected chaptersService: ChaptersService,
    protected convertResourceToJson: ConvertResourceToJsonPipe,
    protected pveFilterPipe: PveFilterPipe
  ) {
    super(chaptersService, convertResourceToJson, pveFilterPipe);
  }


  /**
   * Get project id.
   *
   * @returns Project id.
   */
  public getId(): Project['_id'] {
    return this.get()?._id;
  }

  /**
   * Get project key.
   *
   * @returns Project key.
   */
  public getKey(): Project['key'] {
    return this.get().key;
  }

  /**
   * Get project name.
   *
   * @returns Project name.
   */
  public getName(): Project['name'] {
    return this.get().name;
  }

  /**
   * Get project quality level.
   *
   * @returns Project quality level.
   */
  public getQualityLevel(): Project['qualityLevel'] {
    return this.get().qualityLevel;
  }

  /**
   * Get project version id.
   *
   * @returns Project version id.
   */
  public getVersionId(): Project['versionId'] {
    return this.get().versionId;
  }

  /**
   * Add area.
   *
   * @param area The new area.
   */
  public addArea(area: Area): void {
    const project = this.get();
    let cluster: Cluster;
    let building: Building;
    for (const b of project.buildings) {
      const findCluster = b.clusters.find(c => c._id === area.clusterId);
      if (findCluster) {
        cluster = findCluster;
        building = b;
      }
    }
    const buildingIndex = project.buildings.findIndex(b => b._id === cluster.buildingId);
    const clusterIndex = building.clusters.findIndex(c => c._id === cluster._id);
    project.buildings[buildingIndex].clusters[clusterIndex].areas.push(area);
    this._project.next(project);
  }


  /**
   * Add cluster.
   *
   * @todo Add helper function?
   *
   * @param cluster The new cluster.
   */
  public addCluster(cluster: Cluster): void {
    const project = this.get();
    const buildingIndex = project.buildings.findIndex(b => b._id === cluster.buildingId);
    project.buildings[buildingIndex].clusters.push(cluster);
    this._project.next(project);
  }

  /**
   * Add element.
   *
   * The element is always added to the end of the selected submodule.
   *
   * @param element The element to add.
   */
  public addElement(element: Partial<ProjectElement>): void {
    const project = this.get();
    const moduleIndex = project.modules.findIndex(m => m._id === element.moduleId);
    const module = project.modules[moduleIndex];
    const submoduleIndex = module.submodules.findIndex(s => s._id === element.submoduleId);
    project.modules[moduleIndex].submodules[submoduleIndex].elements.push(element as ProjectElement);
    this.set(project);
  }

  /**
   * Add module.
   *
   * The module is always added to the end of the current project.
   *
   * @param module The module to add.
   */
  public addModule(module: Partial<ProjectModule>): void {
    const project = this.get();
    project.modules.push(module as ProjectModule);
    this.set(project);
  }

  /**
   * Add submodule.
   *
   * The submodule is always added to the end of the selected submodule.
   *
   * @param submodule The submodule to add.
   */
  public addSubmodule(submodule: Partial<ProjectSubmodule>): void {
    const project = this.get();
    const moduleIndex = project.modules.findIndex(m => m._id === submodule.moduleId);

    project.modules[moduleIndex].submodules.push(submodule as ProjectSubmodule);
    this.set(project);
  }

  /**
   * Move area in cluster.
   *
   * @param area The area to be moved.
   * @param fromCluster The updated previous cluster.
   * @param toCluster The updated new cluster.
   */
  public moveArea(area: Area, fromCluster: Cluster, toCluster: Cluster): void {
    const project = this.get();
    const building = project.buildings.find(b => b._id === area.buildingId);
    const buildingIndex = project.buildings.findIndex(b => b._id === area.buildingId);
    const fromClusterIndex = building.clusters.findIndex(c => c._id === fromCluster._id);
    const toClusterIndex = building.clusters.findIndex(c => c._id === toCluster._id);

    project.buildings[buildingIndex].clusters[fromClusterIndex] = fromCluster;
    project.buildings[buildingIndex].clusters[toClusterIndex] = toCluster;
    this.set(project);
  }

  /**
   * Move cluster in project.
   *
   * @param cluster The cluster to move.
   * @param from The cluster's current location.
   * @param to The cluster's new location.
   */
  public moveCluster(cluster: Cluster, from: ClusterDragDropLocation['from'], to: ClusterDragDropLocation['to']): void {
    const project = this.get();
    const building = project.buildings.find(b => b._id === cluster.buildingId);

    moveItemInArray(building.clusters, from, to);
    this.set(project);
  }

  /**
   * Move element in project.
   *
   * @todo Could probably be more efficient, but it works..
   *
   * @param element The element to move.
   * @param fromSubmodule The updated previous submodule.
   * @param toSubmodule The updated new submodule.
   */
  public moveElement(element: ProjectElement, fromSubmodule: ProjectSubmodule, toSubmodule: ProjectSubmodule): void {
    const project = this.get();
    const fromModule = project.modules.find(m => m._id === fromSubmodule.moduleId);
    const toModule = project.modules.find(m => m._id === toSubmodule.moduleId);
    const fromModuleIndex = project.modules.findIndex(m => m._id === fromSubmodule.moduleId);
    const toModuleIndex = project.modules.findIndex(m => m._id === toSubmodule.moduleId);
    const fromSubmoduleIndex = fromModule.submodules.findIndex(sm => sm._id === fromSubmodule._id);
    const toSubmoduleIndex = toModule.submodules.findIndex(sm => sm._id === toSubmodule._id);
    const toSubmoduleElementIndex = toSubmodule.elements.findIndex(e => e._id === element._id);

    // Update module and submodule id's when moving the element.
    toSubmodule.elements[toSubmoduleElementIndex] = {
      ...element,
      moduleId: toSubmodule.moduleId,
      submoduleId: toSubmodule._id
    };
    project.modules[fromModuleIndex].submodules[fromSubmoduleIndex] = fromSubmodule;
    project.modules[toModuleIndex].submodules[toSubmoduleIndex] = toSubmodule;
    this.set(project);
  }

  /**
   * Check if the current project is archived.
   *
   * @returns Whether the current project is archived.
   */
  public isArchived(): Project['archived'] {
    return this.get().archived;
  }

  /**
   * Patch area.
   *
   * @param area The updated area.
   */
  public patchArea(area: Area, values: Partial<Area>): void {
    this.set(set(this.get(), this._getAreaLocation(area), {
      ...area,
      ...values
    }));
  }

  /**
   * Patch basic area.
   *
   * @param basicArea The updated basic area.
   */
  public patchBasicArea(basicArea: BasicArea, values: Partial<BasicArea>): void {
    this.set(set(this.get(), this._getBasicAreaLocation(basicArea), {
      ...basicArea,
      ...values
    }));
  }

  /**
   * Patch building.
   *
   * @param building The updated building.
   */
  public patchBuilding(building: Building, values: Partial<Building>): void {
    this.set(set(this.get(), this._getBuildingLocation(building), {
      ...building,
      ...values
    }));
  }

  /**
   * Patch cluster.
   *
   * @param cluster The updated cluster.
   */
  public patchCluster(cluster: Cluster, values: Partial<Cluster>): void {
    this.set(set(this.get(), this._getClusterLocation(cluster), {
      ...cluster,
      ...values
    }));
  }

  /**
   * Patch element.
   *
   * @param element The updated element.
   */
  public patchElement(element: ProjectElement, values: Partial<ProjectElement>): void {
    this.set(set(this.get(), this._getElementLocation(element), {
      ...element,
      ...values
    }));
  }

  /**
   * Patch module.
   *
   * @param module The updated module.
   */
  public patchModule(module: ProjectModule, values: Partial<ProjectModule>): void {
    this.set(set(this.get(), this._getModuleLocation(module), {
      ...module,
      ...values
    }));
  }

  /**
   * Patch submodule.
   *
   * @param submodule The updated submodule.
   */
  public patchSubmodule(submodule: ProjectSubmodule, values: Partial<ProjectSubmodule>): void {
    this.set(set(this.get(), this._getSubmoduleLocation(submodule), {
      ...submodule,
      ...values
    }));
  }
}
